import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from '../hooks/useAnalytics';
import authService from '../services/authService';
import featureFlags from '../config/featureFlags';
import Button from './shared/Button';
import './LandingPage.css';

const LandingPage = () => {
  const navigate = useNavigate();
  const { trackClick, trackEngagement } = useAnalytics();
  const [user, setUser] = useState(null);

  // Check if user is authenticated
  useEffect(() => {
    if (authService.isAuthenticated()) {
      const userData = authService.getUser();
      setUser(userData);
    }
  }, []);

  // Track how long users stay on landing page
  useEffect(() => {
    const engagementTimer = setTimeout(() => {
      // User stayed for 3+ seconds = engaged
      trackEngagement('landing_page_viewed', { duration_seconds: 3 });
    }, 3000);

    return () => clearTimeout(engagementTimer);
  }, [trackEngagement]);

  // Track Create Game button click
  const handleCreateGame = (e) => {
    e.preventDefault();
    trackClick('create_game_button', 'primary_cta');
    navigate('/create-room');
  };

  // Track Join Game button click
  const handleJoinGame = (e) => {
    e.preventDefault();
    trackClick('join_game_button', 'primary_cta');
    navigate('/join-game');
  };

  // Track Deck Builder button click
  const handleDeckBuilder = (e) => {
    e.preventDefault();
    trackClick('deck_builder_button', 'secondary_cta');
    navigate('/deck-builder');
  };

  // Handle sign out
  const handleSignOut = () => {
    authService.logout();
    setUser(null);
    navigate('/');
  };

  // Handle username change
  const handleChangeUsername = () => {
    // Reset usernameConfirmed flag so user can change their name
    const currentUser = authService.getUser();
    if (currentUser) {
      currentUser.usernameConfirmed = false;
      localStorage.setItem('user', JSON.stringify(currentUser));
    }
    // Redirect to username selection page
    navigate('/choose-username');
  };

  return (
    <div className="page-container">
      <div className="screen-card">
        {user && (
          <div className="user-info">
            <span className="welcome-text">Welcome, {user.username}!</span>
            <div className="user-actions">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/settings')}
                className="settings-button"
              >
                ⚙️ Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleChangeUsername}
                className="change-username-button"
              >
                Change Name
              </Button>
              <Button variant="ghost" size="sm" onClick={handleSignOut} className="sign-out-button">
                Sign Out
              </Button>
            </div>
          </div>
        )}
        <div className="brand-header">
          <h1 className="brand-title">Carnivores Adapting Terribly</h1>
          <p className="brand-subtitle">The hilarious card game, now online</p>
        </div>

        <div className="landing-content">
          <p className="description">
            Create outrageous combinations, compete with friends, and discover why carnivores are
            hilariously bad at adapting to modern life.
          </p>

          <div className="action-buttons">
            <Link to="/create-room" className="btn btn-primary" onClick={handleCreateGame}>
              Create Game
            </Link>
            <Link to="/join-game" className="btn btn-secondary" onClick={handleJoinGame}>
              Join Game
            </Link>
          </div>

          <div className="deck-builder-cta">
            <Link to="/deck-builder" className="btn btn-deck-builder" onClick={handleDeckBuilder}>
              😏 Deck Around
            </Link>
            <p className="deck-builder-subtitle">Create your own custom deck</p>
          </div>

          {featureFlags.ENABLE_KOFI && featureFlags.KOFI_USERNAME && (
            <div className="kofi-section">
              <p className="kofi-message">Enjoying the game? Support development!</p>
              <a
                href={`https://ko-fi.com/${featureFlags.KOFI_USERNAME}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-kofi"
                onClick={() => trackClick('kofi_button', 'donation_cta')}
              >
                ☕ Support on Ko-fi
              </a>
              <p className="kofi-disclaimer">
                Donations are voluntary and non-refundable. They support server costs and
                development.
              </p>
            </div>
          )}

          <div className="features">
            <div className="feature">
              <h2>🎭 Hilarious Cards</h2>
              <p>Hundreds of absurd combinations</p>
            </div>
            <div className="feature">
              <h2>👥 Play with Friends</h2>
              <p>Multiplayer fun online</p>
            </div>
            <div className="feature">
              <h2>⚡ Instant Setup</h2>
              <p>No downloads, just pure fun</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
